// src/utils/calculations.js

// Escape user-provided text before inserting into HTML email templates.
// Prevents XSS / HTML injection in outbound emails.
export const escapeHtml = (str) => {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

export const formatCurrency = (num) => {
  const n = typeof num === 'string' ? parseFloat(num) : num;
  if (n == null || !isFinite(n)) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
};

// Safe number coercion — prevents NaN/Infinity from reaching toFixed(), display templates, etc.
export const safeNumber = (num, fallback = 0) => {
  if (num == null) return fallback;
  const n = typeof num === 'string' ? parseFloat(num) : num;
  return isFinite(n) ? n : fallback;
};

// Portfolio helpers — coerce inputs via safeNumber and guard division by zero.
// flipInvestment returns purchasePrice + rehabCost (the typical ROI denominator).
// calcFlipROI returns the ROI percentage, or 0 when the denominator is non-positive.
export const flipInvestment = (purchasePrice, rehabCost) =>
  safeNumber(purchasePrice) + safeNumber(rehabCost);

export const calcFlipROI = (profit, purchasePrice, rehabCost) => {
  const denom = flipInvestment(purchasePrice, rehabCost);
  return denom > 0 ? (safeNumber(profit) / denom) * 100 : 0;
};

export const calculateCarryCosts = (totalInvestment, interestRate = 6, months = 3) => {
  return ((totalInvestment * (interestRate / 100)) / 12) * months;
};

// Sum a project's logged expenses safely. Single source of truth for
// `project.expenses.reduce(...)` patterns scattered across the codebase.
// `safeNumber` handles string-typed Firestore amounts (some legacy docs store
// `amount` as a string) — bare `e.amount || 0` would concatenate, not add.
export const getProjectActualExpenses = (project) =>
  Array.isArray(project?.expenses)
    ? project.expenses.reduce((sum, e) => sum + safeNumber(e?.amount), 0)
    : 0;

export const calculateROI = (profit, totalInvestment) => {
  if (!totalInvestment || totalInvestment === 0) return 0;
  return (profit / totalInvestment) * 100;
};

export const calculateMAO = (estimatedARV, estimatedRehab) => {
  const arv = parseFloat(estimatedARV) || 0;
  const rehab = parseFloat(estimatedRehab) || 0;
  if (arv === 0) return null;
  return arv * 0.7 - rehab;
};

export const calculateFlipAnalysis = (flipAnalysis) => {
  const purchase = parseFloat(flipAnalysis.purchasePrice) || 0;
  const arv = parseFloat(flipAnalysis.arv) || 0;
  const rehab = parseFloat(flipAnalysis.rehabCost) || 0;
  const holdingMonths = parseFloat(flipAnalysis.holdingMonths) || 0;
  const holdingPerMonth = parseFloat(flipAnalysis.holdingCostPerMonth) || 0;
  const targetMargin =
    flipAnalysis.targetProfitMargin != null
      ? isFinite(parseFloat(flipAnalysis.targetProfitMargin))
        ? parseFloat(flipAnalysis.targetProfitMargin)
        : 10
      : 10;
  const targetProfitMode = flipAnalysis.targetProfitMode || 'percent';

  // Buying costs: dollar mode or percent mode
  const buyingCostsPct = (parseFloat(flipAnalysis.buyingCosts) || 0) / 100;
  const buyingCosts =
    flipAnalysis.buyingCostsMode === 'dollar'
      ? parseFloat(flipAnalysis.buyingCosts) || 0
      : purchase * buyingCostsPct;
  const holdingCosts = holdingMonths * holdingPerMonth;
  // Selling costs: dollar mode or percent mode
  const sellingCosts =
    flipAnalysis.sellingCostsMode === 'dollar'
      ? parseFloat(flipAnalysis.sellingCosts) || 0
      : arv * ((parseFloat(flipAnalysis.sellingCosts) || 0) / 100);

  // Financing costs (based on entered purchase price)
  const loanPct = (parseFloat(flipAnalysis.loanAmount) || 0) / 100;
  const pointsPct = (parseFloat(flipAnalysis.points) || 0) / 100;
  const monthlyRate = (parseFloat(flipAnalysis.interestRate) || 0) / 100 / 12;
  let financingCosts = 0;
  if (flipAnalysis.financingType === 'loan') {
    const loanAmt = purchase * loanPct;
    const points = loanAmt * pointsPct;
    const interest = loanAmt * monthlyRate * holdingMonths;
    financingCosts = isFinite(points + interest) ? points + interest : 0;
  }

  const totalCosts = purchase + rehab + buyingCosts + holdingCosts + sellingCosts + financingCosts;
  const profit = arv - totalCosts;

  // ROI: leverage-aware (cash-on-cash for loan deals)
  let roi = 0;
  if (purchase + rehab > 0) {
    if (flipAnalysis.financingType === 'loan') {
      const cashInvested = purchase * (1 - loanPct) + rehab;
      roi = cashInvested > 0 ? (profit / cashInvested) * 100 : 0;
    } else {
      roi = (profit / (purchase + rehab)) * 100;
    }
  }

  // MAO: solve algebraically so buying costs and financing costs are based on MAO, not entered price
  // MAO = ARV - rehab - fixedCosts - (ARV * targetMargin%) - MAO * variableCoefficient
  // MAO * (1 + variableCoefficient) = ARV - rehab - fixedCosts - (ARV * targetMargin%)
  // MAO = remaining / (1 + variableCoefficient)
  const fixedCosts =
    holdingCosts +
    sellingCosts +
    (flipAnalysis.buyingCostsMode === 'dollar' ? parseFloat(flipAnalysis.buyingCosts) || 0 : 0);
  let variableCoefficient = flipAnalysis.buyingCostsMode === 'dollar' ? 0 : buyingCostsPct;
  if (flipAnalysis.financingType === 'loan') {
    variableCoefficient += loanPct * pointsPct + loanPct * monthlyRate * holdingMonths;
  }
  const minProfit = targetProfitMode === 'dollar' ? targetMargin : (arv * targetMargin) / 100;
  const maoRemaining = arv - rehab - fixedCosts - minProfit;
  const maxPurchase = variableCoefficient > -1 ? maoRemaining / (1 + variableCoefficient) : 0;

  return {
    buyingCosts,
    holdingCosts,
    sellingCosts,
    financingCosts,
    totalCosts,
    profit,
    roi,
    maxPurchase,
  };
};

/**
 * BRRRR Analysis — calculates sweat equity, refinance breakdown, and monthly cash flow.
 * Reuses shared flip inputs (purchase, arv, rehab, costs, financing) and adds
 * rental income, operating expenses, refinance terms.
 */
export const calculateBRRRRAnalysis = (analysis) => {
  const purchase = parseFloat(analysis.purchasePrice) || 0;
  const arv = parseFloat(analysis.arv) || 0;
  const rehab = parseFloat(analysis.rehabCost) || 0;
  const holdingMonths = parseFloat(analysis.holdingMonths) || 0;
  const holdingPerMonth = parseFloat(analysis.holdingCostPerMonth) || 0;

  // Buying costs (same logic as flip)
  const buyingCosts =
    analysis.buyingCostsMode === 'dollar'
      ? parseFloat(analysis.buyingCosts) || 0
      : purchase * ((parseFloat(analysis.buyingCosts) || 0) / 100);
  const holdingCosts = holdingMonths * holdingPerMonth;

  // Financing costs during rehab (hard money)
  const loanPct = (parseFloat(analysis.loanAmount) || 0) / 100;
  const pointsPct = (parseFloat(analysis.points) || 0) / 100;
  const monthlyRate = (parseFloat(analysis.interestRate) || 0) / 100 / 12;
  let financingCosts = 0;
  let hardMoneyBalance = 0;
  if (analysis.financingType === 'loan') {
    const loanAmt = purchase * loanPct;
    hardMoneyBalance = loanAmt;
    const points = loanAmt * pointsPct;
    const interest = loanAmt * monthlyRate * holdingMonths;
    financingCosts = isFinite(points + interest) ? points + interest : 0;
  }

  // Total cash invested (investor's out-of-pocket — no selling costs in BRRRR)
  const downPayment = analysis.financingType === 'loan' ? purchase * (1 - loanPct) : purchase;
  const totalCashInvested = downPayment + rehab + buyingCosts + holdingCosts + financingCosts;

  // Sweat equity = ARV - total project cost (including loan portion)
  const totalProjectCost = purchase + rehab + buyingCosts + holdingCosts + financingCosts;
  const sweatEquity = arv - totalProjectCost;

  // Refinance
  const refiLtv = (parseFloat(analysis.refiLtv) || 75) / 100;
  const refiLoanAmount = arv * refiLtv;
  const cashBackAtRefi = Math.max(0, refiLoanAmount - hardMoneyBalance);
  const moneyLeftInDeal = totalCashInvested - cashBackAtRefi;

  // Monthly mortgage on refi loan (standard amortization: M = P * r(1+r)^n / ((1+r)^n - 1))
  const refiRate = (parseFloat(analysis.refiInterestRate) || 7) / 100 / 12;
  const refiTermMonths = (parseFloat(analysis.refiLoanTerm) || 30) * 12;
  let monthlyMortgage = 0;
  if (refiLoanAmount > 0 && refiRate > 0 && refiTermMonths > 0) {
    const factor = Math.pow(1 + refiRate, refiTermMonths);
    monthlyMortgage = (refiLoanAmount * (refiRate * factor)) / (factor - 1);
  }

  // Monthly cash flow
  const monthlyRent = parseFloat(analysis.monthlyRent) || 0;
  const vacancyRate = (parseFloat(analysis.vacancyRate) || 0) / 100;
  const managementRate = (parseFloat(analysis.managementRate) || 0) / 100;
  const propertyTax = parseFloat(analysis.propertyTaxMonthly) || 0;
  const insurance = parseFloat(analysis.insuranceMonthly) || 0;
  const maintenance = parseFloat(analysis.maintenanceMonthly) || 0;

  const effectiveRent = monthlyRent * (1 - vacancyRate);
  const managementCost = monthlyRent * managementRate;
  const totalOpEx = propertyTax + insurance + maintenance + managementCost;
  const monthlyNOI = effectiveRent - totalOpEx;
  const monthlyCashFlow = monthlyNOI - monthlyMortgage;
  const annualCashFlow = monthlyCashFlow * 12;

  // Cash-on-cash return (guarded for zero/negative money left)
  const cashOnCash =
    moneyLeftInDeal > 0
      ? (annualCashFlow / moneyLeftInDeal) * 100
      : annualCashFlow > 0
        ? Infinity
        : 0;

  return {
    sweatEquity,
    refiLoanAmount,
    totalCashInvested,
    hardMoneyBalance,
    cashBackAtRefi,
    moneyLeftInDeal,
    monthlyMortgage,
    monthlyRent,
    effectiveRent,
    vacancyAmount: monthlyRent * vacancyRate,
    propertyTax,
    insurance,
    maintenance,
    managementCost,
    totalOpEx,
    monthlyNOI,
    monthlyCashFlow,
    annualCashFlow,
    cashOnCash,
  };
};

/**
 * Generate 30-year BRRRR forecast.
 * Applies annual rent growth and appreciation. OpEx held flat (noted in spec as simplification).
 * Uses standard amortization schedule for loan balance.
 */
export const generateBRRRRForecast = (analysis, brrrrResults) => {
  const years = parseFloat(analysis.refiLoanTerm) || 30;
  const refiRate = (parseFloat(analysis.refiInterestRate) || 7) / 100 / 12;
  const rentGrowth = (parseFloat(analysis.annualRentIncrease) || 0) / 100;
  const appreciation = (parseFloat(analysis.annualAppreciation) || 0) / 100;
  const arv = parseFloat(analysis.arv) || 0;

  let loanBalance = brrrrResults.refiLoanAmount;
  let cumulativeCashFlow = 0;
  let currentRent = parseFloat(analysis.monthlyRent) || 0;
  let currentValue = arv;
  const vacancyRate = (parseFloat(analysis.vacancyRate) || 0) / 100;
  const managementRate = (parseFloat(analysis.managementRate) || 0) / 100;
  const propertyTax = parseFloat(analysis.propertyTaxMonthly) || 0;
  const insurance = parseFloat(analysis.insuranceMonthly) || 0;
  const maintenance = parseFloat(analysis.maintenanceMonthly) || 0;
  const monthlyMortgage = brrrrResults.monthlyMortgage;
  const monthlyRent = parseFloat(analysis.monthlyRent) || 0;

  const forecast = [];

  for (let yr = 1; yr <= years; yr++) {
    // Apply growth at start of year (rent grows, value appreciates)
    if (yr > 1) {
      currentRent *= 1 + rentGrowth;
      currentValue *= 1 + appreciation;
    }

    const annualRent = currentRent * 12;
    const effectiveRent = currentRent * (1 - vacancyRate);
    // OpEx held constant per spec (except vacancy which scales with rent)
    const mgmt = monthlyRent * managementRate; // based on original rent, held flat
    const annualOpEx = (propertyTax + insurance + maintenance + mgmt) * 12;
    const annualMortgage = monthlyMortgage * 12;

    // Amortize loan for this year (12 monthly payments)
    for (let m = 0; m < 12; m++) {
      if (loanBalance <= 0) break;
      const interestPayment = loanBalance * refiRate;
      const principalPayment = monthlyMortgage - interestPayment;
      loanBalance = Math.max(0, loanBalance - principalPayment);
    }

    const netCashFlow = effectiveRent * 12 - annualOpEx - annualMortgage;
    cumulativeCashFlow += netCashFlow;
    const equity = currentValue - loanBalance;
    const totalReturn = cumulativeCashFlow + equity - brrrrResults.totalCashInvested;

    forecast.push({
      year: yr,
      rentalIncome: annualRent,
      operatingExpenses: annualOpEx,
      mortgagePayment: annualMortgage,
      netCashFlow,
      cumulativeCashFlow,
      propertyValue: currentValue,
      loanBalance,
      equity,
      totalReturn,
    });
  }

  return forecast;
};
